/**
 * PUMA Name Mapping Service
 * Maps PUMA codes to their actual names for Bay Area PUMAs
 * Based on 2020 Census PUMA definitions
 */

import { CALIFORNIA_REGIONS } from './regionMapping';
import { getCountyFromPumaGeoJsonSync } from './pumaGeoJsonService';

export interface PumaInfo {
  code: number;
  name: string;
  county: string;
  description?: string;
}

/**
 * Bay Area PUMA names and information
 * Source: U.S. Census Bureau 2023 PUMA definitions (from california_pumas_2023.geojson)
 */
export const BAY_AREA_PUMA_NAMES: Record<number, PumaInfo> = {
  // Alameda County PUMAs
  101: { code: 101, name: "Alameda County (North) - Berkeley & Albany Cities", county: "Alameda" },
  111: { code: 111, name: "Alameda County (Northwest) - Oakland City (Downtown/West Oakland/North Oakland)", county: "Alameda" },
  112: { code: 112, name: "Alameda County (Northwest) - Oakland City (Oakland Hills)", county: "Alameda" },
  113: { code: 113, name: "Alameda County (West) - Oakland City (Elmhurst/Central/East Oakland)", county: "Alameda" },
  114: { code: 114, name: "Alameda County (West) - San Leandro, Alameda, Emeryville & Piedmont Cities", county: "Alameda" },
  115: { code: 115, name: "Alameda County (Northwest) - Castro Valley & Ashland", county: "Alameda" },
  116: { code: 116, name: "Alameda County (Central) - Hayward City (East)", county: "Alameda" },
  117: { code: 117, name: "Alameda County (South) - Newark, Union City (West) & Fremont (West) Cities", county: "Alameda" },
  118: { code: 118, name: "Alameda County (South) - Fremont (Northeast) & Union City (East) Cities", county: "Alameda" },
  119: { code: 119, name: "Alameda County (Central) - Pleasanton & Dublin (West) Cities", county: "Alameda" },
  120: { code: 120, name: "Alameda County (East) - Livermore & Dublin (East) Cities", county: "Alameda" },
  121: { code: 121, name: "Alameda County (South) - Fremont City (Southeast)", county: "Alameda" },
  122: { code: 122, name: "Alameda County (West) - Hayward City (West)", county: "Alameda" },
  123: { code: 123, name: "Alameda County (Northwest) - Oakland City (San Antonio/Fruitvale)", county: "Alameda" },

  // Contra Costa County PUMAs
  1301: { code: 1301, name: "Contra Costa County (Far Southwest) - Richmond (Southwest) & San Pablo Cities", county: "Contra Costa" },
  1305: { code: 1305, name: "Contra Costa County (South) - San Ramon City & Danville Town", county: "Contra Costa" },
  1308: { code: 1308, name: "Contra Costa County (Northeast) - Antioch City", county: "Contra Costa" },
  1309: { code: 1309, name: "Contra Costa County (East) - Brentwood & Oakley Cities", county: "Contra Costa" },
  1310: { code: 1310, name: "Contra Costa County (Far Northwest) - Richmond (North), Hercules & El Cerrito Cities", county: "Contra Costa" },
  1311: { code: 1311, name: "Contra Costa County (West) - Concord (West), Martinez & Pleasant Hill Cities", county: "Contra Costa" },
  1312: { code: 1312, name: "Contra Costa County (West) - Walnut Creek (West), Lafayette & Orinda Cities", county: "Contra Costa" },
  1313: { code: 1313, name: "Contra Costa County (Central) - Concord (South), Walnut Creek (East) & Clayton Cities", county: "Contra Costa" },
  1314: { code: 1314, name: "Contra Costa County (North) - Pittsburg & Concord (North) Cities", county: "Contra Costa" },

  // Marin County PUMAs
  4103: { code: 4103, name: "Marin County (North & West) - Novato & San Rafael (North) Cities", county: "Marin" },
  4104: { code: 4104, name: "Marin County (Southeast) - San Rafael (South), Mill Valley & Sausalito Cities", county: "Marin" },

  // Napa County PUMAs
  5500: { code: 5500, name: "Napa County", county: "Napa", description: "Napa County unincorporated areas" },

  // San Francisco County PUMAs
  7507: { code: 7507, name: "San Francisco County (South Central) - Bayview & Hunters Point", county: "San Francisco" },
  7508: { code: 7508, name: "San Francisco County (Northwest) - Richmond, Western Addition & Presidio", county: "San Francisco" },
  7509: { code: 7509, name: "San Francisco County (Northeast) - Chinatown, North Beach & Russian Hill", county: "San Francisco" },
  7510: { code: 7510, name: "San Francisco County (Southeast) - South of Market & Mission", county: "San Francisco" },
  7511: { code: 7511, name: "San Francisco County (Southwest) - Central & Bernal Heights", county: "San Francisco" },
  7512: { code: 7512, name: "San Francisco County (Southwest) - Outer Sunset & Inner Sunset", county: "San Francisco" },
  7513: { code: 7513, name: "San Francisco County (Southwest) - Ingleside & South Central", county: "San Francisco" },
  7514: { code: 7514, name: "San Francisco County (Northwest) - Western Addition & Marina", county: "San Francisco" },

  // San Mateo County PUMAs
  8101: { code: 8101, name: "San Mateo County (North Central) - Daly City, Pacifica Cities & Colma Town", county: "San Mateo" },
  8102: { code: 8102, name: "San Mateo County (North Central) - South San Francisco, San Bruno & Brisbane Cities", county: "San Mateo" },
  8103: { code: 8103, name: "San Mateo County (Central) - San Mateo (North), Burlingame & Millbrae Cities", county: "San Mateo" },
  8104: { code: 8104, name: "San Mateo County (South & West) - San Mateo (South) & Half Moon Bay Cities", county: "San Mateo" },
  8105: { code: 8105, name: "San Mateo County (East Central) - Redwood City, San Carlos & Belmont Cities", county: "San Mateo" },
  8106: { code: 8106, name: "San Mateo County (Southeast) - Menlo Park, East Palo Alto Cities & Atherton Town", county: "San Mateo" },

  // Santa Clara County PUMAs
  8505: { code: 8505, name: "Santa Clara County (North Central) - San Jose City (East Central) & Alum Rock", county: "Santa Clara" },
  8506: { code: 8506, name: "Santa Clara County (East) - Gilroy, Morgan Hill & San Jose (South) Cities", county: "Santa Clara" },
  8507: { code: 8507, name: "Santa Clara County (Southwest) - Cupertino, Saratoga Cities & Los Gatos Town", county: "Santa Clara" },
  8508: { code: 8508, name: "Santa Clara County (Central) - San Jose (West Central) & Campbell Cities", county: "Santa Clara" },
  8510: { code: 8510, name: "Santa Clara County (Central) - San Jose City (Central)", county: "Santa Clara" },
  8511: { code: 8511, name: "Santa Clara County (Central) - San Jose City (South Central/Branham) & Cambrian Park", county: "Santa Clara" },
  8512: { code: 8512, name: "Santa Clara County (Central) - San Jose City (Southwest/Almaden Valley)", county: "Santa Clara" },
  8515: { code: 8515, name: "Santa Clara County (Northwest) - Palo Alto City & Los Altos Hills Town", county: "Santa Clara" },
  8516: { code: 8516, name: "Santa Clara County (Northwest) - Sunnyvale City", county: "Santa Clara" },
  8517: { code: 8517, name: "Santa Clara County (Northwest) - Santa Clara City", county: "Santa Clara" },
  8518: { code: 8518, name: "Santa Clara County (Northwest) - San Jose (Berryessa) & Milpitas Cities", county: "Santa Clara" },
  8519: { code: 8519, name: "Santa Clara County (Central) - San Jose City (North)", county: "Santa Clara" },
  8520: { code: 8520, name: "Santa Clara County (Central) - San Jose City (Southeast/Evergreen)", county: "Santa Clara" },
  8521: { code: 8521, name: "Santa Clara County (North) - San Jose City (North Valley/East San Jose)", county: "Santa Clara" },
  8522: { code: 8522, name: "Santa Clara County (Northwest) - Mountain View & Los Altos Cities", county: "Santa Clara" },

  // Solano County PUMAs
  9501: { code: 9501, name: "Solano County (Southwest) - Vallejo & Benicia Cities", county: "Solano" },
  9502: { code: 9502, name: "Solano County (Central) - Fairfield & Suisun City Cities", county: "Solano" },
  9503: { code: 9503, name: "Solano County (Northeast) - Vacaville & Dixon Cities", county: "Solano" },

  // Sonoma County PUMAs
  9702: { code: 9702, name: "Santa Rosa City", county: "Sonoma", description: "Santa Rosa city limits" },
  9704: { code: 9704, name: "Petaluma City", county: "Sonoma", description: "Petaluma city limits" },
  9705: { code: 9705, name: "Rohnert Park City", county: "Sonoma", description: "Rohnert Park city limits" },
  9706: { code: 9706, name: "Sonoma County Unincorporated", county: "Sonoma", description: "Sonoma County unincorporated areas" }
};

/**
 * Get PUMA name from code
 */
export function getPumaName(pumaCode: number): string {
  const pumaInfo = BAY_AREA_PUMA_NAMES[pumaCode];
  return pumaInfo ? pumaInfo.name : `PUMA ${pumaCode}`;
}

/**
 * Get PUMA info (name, county, description) from code
 */
export function getPumaInfo(pumaCode: number): PumaInfo | null {
  return BAY_AREA_PUMA_NAMES[pumaCode] || null;
}

/**
 * Comprehensive PUMA-to-County mapping for all California PUMAs
 * Handles both short codes (101, 1301) and 6-digit codes (600101, 601301)
 * Source: PUMA code to county documentation
 */
const COMPREHENSIVE_PUMA_TO_COUNTY: Record<number, string> = {
  // Alameda County - handle both formats
  600101: 'Alameda',
  600102: 'Alameda',
  600103: 'Alameda',
  600104: 'Alameda',
  600105: 'Alameda',
  101: 'Alameda', 111: 'Alameda', 112: 'Alameda', 113: 'Alameda', 114: 'Alameda',
  115: 'Alameda', 116: 'Alameda', 117: 'Alameda', 118: 'Alameda', 119: 'Alameda',
  120: 'Alameda', 121: 'Alameda', 122: 'Alameda', 123: 'Alameda',
  
  // Contra Costa County - handle both formats
  601301: 'Contra Costa',
  601302: 'Contra Costa',
  601303: 'Contra Costa',
  601304: 'Contra Costa',
  1301: 'Contra Costa', 1305: 'Contra Costa', 1308: 'Contra Costa', 1309: 'Contra Costa',
  1310: 'Contra Costa', 1311: 'Contra Costa', 1312: 'Contra Costa', 1313: 'Contra Costa', 1314: 'Contra Costa',
  
  // San Francisco County - handle both formats
  607501: 'San Francisco',
  607502: 'San Francisco',
  607503: 'San Francisco',
  607504: 'San Francisco',
  7507: 'San Francisco', 7508: 'San Francisco', 7509: 'San Francisco', 7510: 'San Francisco',
  7511: 'San Francisco', 7512: 'San Francisco', 7513: 'San Francisco', 7514: 'San Francisco',
  
  // Santa Clara County - handle both formats
  608501: 'Santa Clara',
  608502: 'Santa Clara',
  608503: 'Santa Clara',
  608504: 'Santa Clara',
  608505: 'Santa Clara',
  8505: 'Santa Clara', 8506: 'Santa Clara', 8507: 'Santa Clara', 8508: 'Santa Clara', 8510: 'Santa Clara',
  8511: 'Santa Clara', 8512: 'Santa Clara', 8515: 'Santa Clara', 8516: 'Santa Clara', 8517: 'Santa Clara',
  8518: 'Santa Clara', 8519: 'Santa Clara', 8520: 'Santa Clara', 8521: 'Santa Clara', 8522: 'Santa Clara',
  
  // Los Angeles County
  603701: 'Los Angeles',
  603702: 'Los Angeles',
  603703: 'Los Angeles',
  603704: 'Los Angeles',
  603705: 'Los Angeles',
  603706: 'Los Angeles',
  
  // San Diego County
  607301: 'San Diego',
  607302: 'San Diego',
  607303: 'San Diego',
  607304: 'San Diego',
  
  // Marin County - handle both formats
  604101: 'Marin',
  604102: 'Marin',
  4103: 'Marin', 4104: 'Marin',
  
  // Sonoma County - handle both formats
  609701: 'Sonoma',
  609702: 'Sonoma',
  609703: 'Sonoma',
  9702: 'Sonoma', 9704: 'Sonoma', 9705: 'Sonoma', 9706: 'Sonoma',
  
  // Napa County
  5500: 'Napa',
  
  // San Mateo County
  8101: 'San Mateo', 8102: 'San Mateo', 8103: 'San Mateo', 8104: 'San Mateo', 8105: 'San Mateo', 8106: 'San Mateo',
  
  // Solano County
  9501: 'Solano', 9502: 'Solano', 9503: 'Solano',
};

/**
 * Helper function to normalize PUMA code (handle 6-digit format)
 */
function normalizePumaCode(pumaCode: number): number {
  // If it's a 6-digit code starting with 60, extract the PUMA portion
  if (pumaCode >= 600000 && pumaCode <= 699999) {
    // Remove the "60" prefix (state code)
    return parseInt(pumaCode.toString().slice(2));
  }
  return pumaCode;
}

/**
 * Get county/detailed name from PUMA code
 * Uses multiple fallback strategies:
 * 1. GeoJSON mapping (extracted from map data - most comprehensive with detailed names)
 * 2. Bay Area detailed mapping (county names for Bay Area)
 * 3. Comprehensive mapping (from document)
 * 4. Region mapping (fallback)
 */
export function getCountyFromPuma(pumaCode: number): string {
  // PRIORITY 1: Try GeoJSON mapping first (most comprehensive - includes detailed names like "Los Angeles County--LA (Southwest/Marina del Rey")
  // This ensures we get the full detailed name from NAMELSAD20 when available
  const geoJsonCounty = getCountyFromPumaGeoJsonSync(pumaCode);
  if (geoJsonCounty) {
    return geoJsonCounty;
  }
  
  // PRIORITY 2: Check Bay Area mapping (detailed with names for Bay Area PUMAs)
  const pumaInfo = BAY_AREA_PUMA_NAMES[pumaCode];
  if (pumaInfo) {
    return pumaInfo.county;
  }
  
  // PRIORITY 3: Try normalized code for comprehensive mapping (handles 6-digit format)
  const normalizedCode = normalizePumaCode(pumaCode);
  if (COMPREHENSIVE_PUMA_TO_COUNTY[normalizedCode]) {
    return COMPREHENSIVE_PUMA_TO_COUNTY[normalizedCode];
  }
  
  // PRIORITY 4: Try original code in comprehensive mapping
  if (COMPREHENSIVE_PUMA_TO_COUNTY[pumaCode]) {
    return COMPREHENSIVE_PUMA_TO_COUNTY[pumaCode];
  }
  
  // PRIORITY 5: If not in GeoJSON mapping, use region mapping to determine county
  // Find which region this PUMA belongs to
  for (const region of CALIFORNIA_REGIONS) {
    if (region.pumaCodes.includes(pumaCode) || region.pumaCodes.includes(normalizedCode)) {
      // If region has only one county, return that county
      if (region.counties.length === 1) {
        return region.counties[0];
      }
      // If region has multiple counties, return region name (user can see multiple counties)
      // This is better than defaulting to "Bay Area"
      return region.name;
    }
  }
  
  // Default fallback - should rarely be hit for California PUMAs
  return 'California';
}

/**
 * Get all PUMA codes for a specific county
 */
export function getPumasByCounty(county: string): number[] {
  return Object.values(BAY_AREA_PUMA_NAMES)
    .filter(puma => puma.county === county)
    .map(puma => puma.code);
}

/**
 * Get all counties in Bay Area
 */
export function getBayAreaCounties(): string[] {
  const counties = new Set(Object.values(BAY_AREA_PUMA_NAMES).map(puma => puma.county));
  return Array.from(counties).sort();
}

/**
 * Search PUMAs by name (case-insensitive)
 */
export function searchPumasByName(searchTerm: string): PumaInfo[] {
  const term = searchTerm.toLowerCase();
  return Object.values(BAY_AREA_PUMA_NAMES)
    .filter(puma => 
      puma.name.toLowerCase().includes(term) ||
      puma.county.toLowerCase().includes(term) ||
      (puma.description && puma.description.toLowerCase().includes(term))
    );
}
